/**
 * <InzaraEmbed /> — the flagship embed component of @inzara/embed-react.
 *
 * v0.3 hardened bootstrap flow
 * ----------------------------
 * The iframe is loaded at `/embed/v2/bootstrap?target=<resource>[/<id>]`
 * — the JWT is **never** placed in the URL. Once the iframe posts
 * `{source:'inzara.embed', type:'bootstrap.ready'}` back, the SDK
 * replies with `{source:'inzara.host', type:'token.bootstrap', token}`.
 * The embed provider stores the token in memory, then the bootstrap
 * page client-navigates to the real embed route. No `document.referrer`,
 * no browser history, no server access log ever sees the token.
 *
 * Silent refresh (from v0.2) still applies: the SDK schedules a call
 * to `POST /api/embed/v2/refresh` and forwards the new token via a
 * `token.updated` postMessage so the iframe swaps seamlessly.
 *
 * Host contract:
 *   <InzaraEmbed
 *     host="https://analytics.example.com"
 *     token={jwt}
 *     resource="dashboard"
 *     id={dashboardId}
 *     autoRefresh                       // default true
 *     refreshLeadTimeSeconds={60}       // default 60
 *     onTokenRefresh={(newToken, meta) => saveForNextMount(newToken)}
 *     onEvent={ev => console.log(ev)}
 *   />
 *
 * The initial `token` must be minted server-side (host backend calling
 * `/api/mgmt/v1/embed/sessions` with a workspace-scoped management API
 * key). Never mint tokens with the management key in the browser.
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { buildBootstrapTarget, RESOURCES } from './constants.js';
import { useInzaraEvents } from './useInzaraEvents.js';

const VALID_RESOURCES = new Set(Object.values(RESOURCES));

const DEFAULT_SANDBOX = [
  'allow-scripts',
  'allow-same-origin',
  'allow-forms',
  'allow-popups',
  'allow-downloads',
  'allow-popups-to-escape-sandbox',
].join(' ');

const MIN_REFRESH_DELAY_MS = 5_000;
const DEFAULT_LEAD_SECONDS  = 60;

function buildBootstrapSrc(host, resource, id) {
  if (!host) throw new Error('@inzara/embed-react: `host` is required');
  if (!VALID_RESOURCES.has(resource)) {
    throw new Error(
      `@inzara/embed-react: unknown resource '${resource}'. ` +
      `Use one of: ${[...VALID_RESOURCES].join(', ')}`
    );
  }
  const target = buildBootstrapTarget(resource, id);
  const base = host.replace(/\/+$/, '');
  const url = new URL(`${base}/embed/v2/bootstrap`);
  url.searchParams.set('target', target);
  return url.toString();
}

/**
 * Decode a JWT payload (unverified) to read `exp` for refresh scheduling.
 * Returns null for anything malformed.
 */
function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length < 2) return null;
  try {
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const json = typeof atob === 'function'
      ? atob(b64)
      : Buffer.from(b64, 'base64').toString('utf-8');
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export default forwardRef(function InzaraEmbed(props, embedRef) {
  const {
    host,
    token,
    resource,
    id,
    title,
    height = 640,
    width = '100%',
    className,
    style,
    onEvent,
    onLoad,
    onError,
    onDrill,
    onFilter,
    onTokenRefresh,
    autoRefresh = true,
    refreshLeadTimeSeconds = DEFAULT_LEAD_SECONDS,
    refreshTtlSeconds,
    sandbox = DEFAULT_SANDBOX,
    referrerPolicy = 'no-referrer',
    ...rest
  } = props;

  if (!token) {
    throw new Error('@inzara/embed-react: `token` is required');
  }

  // liveToken is what we hand to the iframe — via postMessage — and what
  // gets exposed on `onTokenRefresh`. The iframe URL itself is a stable
  // "bootstrap" URL that never carries a secret, so we don't need to
  // track a separate `srcToken` anymore.
  const [liveToken, setLiveToken] = useState(token);

  useEffect(() => {
    if (token && token !== liveToken) setLiveToken(token);
  }, [token, liveToken]);

  const iframeRef = useRef(null);
  const timerRef  = useRef(null);
  const refreshingRef = useRef(false);
  const liveTokenRef  = useRef(liveToken);
  liveTokenRef.current = liveToken;

  const src = useMemo(
    () => buildBootstrapSrc(host, resource, id),
    [host, resource, id],
  );

  const origin = useMemo(() => {
    try { return new URL(host).origin; } catch { return host; }
  }, [host]);

  // ── Bootstrap handshake ───────────────────────────────────────────
  // The embed page emits `bootstrap.ready` on mount. We reply with the
  // current in-memory token so it can navigate to the real surface.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    function onMessage(ev) {
      if (origin && origin !== '*' && ev.origin !== origin) return;
      const data = ev?.data;
      if (!data || typeof data !== 'object') return;
      if (data.source !== 'inzara.embed') return;
      if (data.type !== 'bootstrap.ready') return;
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'inzara.host', type: 'token.bootstrap', token: liveTokenRef.current },
          origin && origin !== '*' ? origin : '*',
        );
      } catch { /* handshake will retry via useInzaraEvents error surface */ }
    }
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [origin]);

  // Fan-out for user-facing callbacks.
  useInzaraEvents(origin, (ev) => {
    if (onEvent) onEvent(ev);
    const t = ev?.type;
    if (t === 'content.loaded' || t === 'insights.loaded' || t === 'kpi.loaded' || t === 'viewer.identified') {
      if (onLoad) onLoad(ev);
    }
    if (t === 'error') {
      if (onError) onError(ev);
    }
    if (t === 'drill.request') {
      if (onDrill) onDrill(ev);
    }
    if (t === 'filter.applied' || t === 'filter.cleared') {
      if (onFilter) onFilter(ev);
    }
  });

  // ── Host → iframe control (imperative handle) ─────────────────────
  useImperativeHandle(embedRef, () => ({
    /** Programmatically apply a filter inside the embed. */
    applyFilter(key, value) {
      if (!key) return;
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'inzara.host', type: 'filter.set', key, value },
          origin && origin !== '*' ? origin : '*',
        );
      } catch { /* no-op */ }
    },
    /** Clear a specific filter (or omit `key` to clear all). */
    clearFilter(key) {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'inzara.host', type: 'filter.clear', ...(key ? { key } : {}) },
          origin && origin !== '*' ? origin : '*',
        );
      } catch { /* no-op */ }
    },
    /**
     * Confirm to the embed that the host handled a `drill.request`, so
     * a small "return to <label>" breadcrumb chip renders inside the
     * iframe. `ttl_ms` auto-dismisses the chip.
     *
     *   ref.current?.respondToDrill({
     *     target: drillEvent.target,
     *     label:  'Return to customer #42',
     *     ttl_ms: 15000,
     *   });
     */
    respondToDrill(response) {
      if (!response || typeof response.label !== 'string') return;
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'inzara.host', type: 'drill.response', ...response },
          origin && origin !== '*' ? origin : '*',
        );
      } catch { /* no-op */ }
    },
    /** Ask the embed to re-emit its current viewer/session state. */
    ping() {
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'inzara.host', type: 'ping' },
          origin && origin !== '*' ? origin : '*',
        );
      } catch { /* no-op */ }
    },
  }), [origin]);

  // ── Silent token refresh ─────────────────────────────────────────
  const performRefresh = useCallback(async (current) => {
    if (refreshingRef.current || !current) return;
    refreshingRef.current = true;
    try {
      const base = host.replace(/\/+$/, '');
      const body = refreshTtlSeconds ? { ttl_seconds: refreshTtlSeconds } : {};
      const res = await fetch(`${base}/api/embed/v2/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${current}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const b = await res.json().catch(() => ({}));
        const msg = b?.detail?.message || b?.detail || `HTTP ${res.status}`;
        const err = { type: 'error', code: 'token_refresh_failed', message: msg };
        if (onError) onError(err);
        if (onEvent) onEvent({ source: 'inzara.embed', ...err });
        return;
      }
      const payload = await res.json();
      const nextToken = payload?.token;
      if (!nextToken || typeof nextToken !== 'string') return;

      setLiveToken(nextToken);

      // Push the new token to the iframe — its next API call will use
      // it, and the previous jti has already been revoked server-side.
      try {
        iframeRef.current?.contentWindow?.postMessage(
          { source: 'inzara.host', type: 'token.updated', token: nextToken },
          origin && origin !== '*' ? origin : '*',
        );
      } catch { /* callback below still notifies the host */ }

      if (onTokenRefresh) {
        onTokenRefresh(nextToken, {
          jti: payload.jti,
          previous_jti: payload.previous_jti,
          expires_at: payload.expires_at,
        });
      }
      if (onEvent) {
        onEvent({
          source: 'inzara.embed',
          type: 'token.refreshed',
          jti: payload.jti,
          expires_at: payload.expires_at,
        });
      }
    } catch (e) {
      const msg = String(e?.message || e);
      if (onError) onError({ type: 'error', code: 'token_refresh_failed', message: msg });
    } finally {
      refreshingRef.current = false;
    }
  }, [host, origin, refreshTtlSeconds, onError, onEvent, onTokenRefresh]);

  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (!autoRefresh || !liveToken) return undefined;

    const claims = decodeJwtPayload(liveToken);
    const exp = claims?.exp;
    if (!exp || typeof exp !== 'number') return undefined;

    const nowMs   = Date.now();
    const expMs   = exp * 1000;
    const leadMs  = Math.max(0, refreshLeadTimeSeconds * 1000);
    const delayMs = Math.max(MIN_REFRESH_DELAY_MS, expMs - nowMs - leadMs);

    if (expMs <= nowMs) return undefined;

    timerRef.current = setTimeout(() => { performRefresh(liveToken); }, delayMs);
    return () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [liveToken, autoRefresh, refreshLeadTimeSeconds, performRefresh]);

  return (
    <iframe
      ref={iframeRef}
      src={src}
      title={title || `INZARA — ${resource}`}
      width={width}
      height={height ?? undefined}
      className={className}
      style={style}
      sandbox={sandbox}
      referrerPolicy={referrerPolicy}
      loading="lazy"
      {...rest}
    />
  );
});
